<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';
import Banner from 'dashboard/components-next/banner/Banner.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

// Everything the suggestion depends on is decided server side (whether the outage is long enough
// to be worth syncing, and whether this Evolution server is new enough to support it), so an
// empty response is the signal to stay hidden - see InboxesController#evolution_pending_outage.
const outage = ref(null);
const isSyncing = ref(false);

const offlineLabel = computed(() => {
  const seconds = outage.value?.offline_seconds ?? 0;
  const hours = Math.round(seconds / 3600);
  return seconds >= 3600
    ? t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.OUTAGE_SYNC.DURATION_HOURS', {
        count: hours,
      })
    : t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.OUTAGE_SYNC.DURATION_MINUTES', {
        count: Math.max(1, Math.round(seconds / 60)),
      });
});

const fetchPendingOutage = async inboxId => {
  outage.value = null;
  try {
    const { data } = await InboxesAPI.evolutionPendingOutage(inboxId);
    // Switching inboxes while this is in flight would otherwise land one inbox's outage on
    // another's conversation list - and sync the wrong window when clicked.
    if (inboxId !== props.inbox.id) return;
    outage.value = data.since ? data : null;
  } catch (error) {
    // A suggestion nobody asked for shouldn't raise an alert when it can't be loaded.
  }
};

const syncOutage = async () => {
  isSyncing.value = true;
  try {
    await InboxesAPI.evolutionSyncOutage(props.inbox.id);
    outage.value = null;
    useAlert(t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.OUTAGE_SYNC.STARTED'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.OUTAGE_SYNC.ERROR'));
  } finally {
    isSyncing.value = false;
  }
};

watch(() => props.inbox.id, fetchPendingOutage, { immediate: true });
</script>

<template>
  <div v-show="outage" class="px-3 pt-3">
    <Banner
      color="amber"
      :action-label="
        isSyncing
          ? $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.OUTAGE_SYNC.SYNCING')
          : $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.OUTAGE_SYNC.ACTION_LABEL')
      "
      @action="syncOutage"
    >
      {{
        $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.OUTAGE_SYNC.DESCRIPTION', {
          duration: offlineLabel,
        })
      }}
    </Banner>
  </div>
</template>
